import React, { useState, useEffect } from "react";
import { PAGE_SIZE } from "../../../api";
import {
  fetchEntries,
  searchEntries,
} from "../services/KBService";
import Icon from "../../../components/Icon";
import Skeleton from "../../../components/Skeleton";
import { EmptyState, ErrorState } from "../../../components/State";
import Pagination from "../../../components/Pagination";
import SearchInput from "../../../components/SearchInput";
import { KB_CATEGORIES, getKBCategory } from "../components/kb/kbConstants";
import { KBModal } from "../components/kb/KBModal";
import { KBCard } from "../components/kb/KBCard";

export { KB_CATEGORIES, getKBCategory, KBModal, KBCard };

export default function KnowledgeBaseView() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeScope, setActiveScope] = useState("all"); // all | global | org
  const [activeCat, setActiveCat] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchEntries(100)
      .then((data) => setEntries(data?.items || []))
      .catch(() => setError("Failed to load knowledge base."))
      .finally(() => setLoading(false));
  }, []);

  async function handleSearch() {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const results = await searchEntries({ query: searchQuery, top_k: 20 });
      setSearchResults(results || []);
      setPage(1);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  function clearSearch() {
    setSearchResults(null);
    setSearchQuery("");
    setPage(1);
  }

  function handleSaved(saved, isEdit) {
    if (isEdit)
      setEntries((prev) => prev.map((e) => (e.id === saved.id ? saved : e)));
    else setEntries((prev) => [saved, ...prev]);
    setSearchResults(null);
    setPage(1);
  }

  function handleDelete(id) {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    if (searchResults)
      setSearchResults((prev) => prev.filter((r) => r.entry.id !== id));
  }

  // Base entries (search or full list)
  const baseEntries = searchResults
    ? searchResults.map((r) => r.entry)
    : entries;

  // Filter by scope
  const scopeFiltered =
    activeScope === "global"
      ? baseEntries.filter((e) => e.is_global)
      : activeScope === "org"
        ? baseEntries.filter((e) => !e.is_global)
        : baseEntries;

  // Filter by category
  const catFiltered =
    activeCat === "all"
      ? scopeFiltered
      : scopeFiltered.filter((e) => getKBCategory(e.issue_type) === activeCat);

  // Count per category from scope-filtered
  const catCounts = Object.keys(KB_CATEGORIES).reduce((acc, k) => {
    acc[k] = scopeFiltered.filter(
      (e) => getKBCategory(e.issue_type) === k,
    ).length;
    return acc;
  }, {});

  // Pagination
  const paged = catFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Group search results by category
  const groupedSearch = searchResults
    ? catFiltered.reduce((acc, e) => {
        const c = getKBCategory(e.issue_type);
        if (!acc[c]) acc[c] = [];
        acc[c].push(e);
        return acc;
      }, {})
    : null;

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "18px 24px 14px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 14,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Knowledge Base
            </h1>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6B7280" }}>
              {entries.length} {entries.length === 1 ? "entry" : "entries"} —
              runbooks, root causes, and resolutions
            </p>
          </div>
          {entries.length > 0 && (
            <button
              onClick={() => {
                setEditEntry(null);
                setShowModal(true);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "8px 16px",
                background: "#1D4ED8",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <Icon name="plus" size={14} color="#fff" /> Add entry
            </button>
          )}
        </div>

        {/* Search & Scope toggle (Toolbar) */}
        {entries.length > 0 && (
          <>
            {/* Search */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <SearchInput
                placeholder="Search by symptom, error type, or issue…"
                value={searchQuery}
                onChange={(val) => {
                  setSearchQuery(val);
                  if (!val) clearSearch();
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{ flex: 1 }}
              />
              <button
                onClick={handleSearch}
                disabled={searching}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "8px 16px",
                  background: "#1D4ED8",
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: searching ? "not-allowed" : "pointer",
                }}
              >
                <Icon name="search" size={13} color="#fff" />
                {searching ? "Searching…" : "Search"}
              </button>
              {searchResults && (
                <button
                  onClick={clearSearch}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: 7,
                    background: "#fff",
                    fontSize: 12,
                    cursor: "pointer",
                    color: "#6B7280",
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            {searchResults && (
              <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 10 }}>
                {searchResults.length > 0 ? (
                  <>
                    <strong>{searchResults.length}</strong> results for "
                    <em>{searchQuery}</em>"
                  </>
                ) : (
                  <>
                    No results for "<em>{searchQuery}</em>"
                  </>
                )}
              </div>
            )}

            {/* Scope toggle */}
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: "#9CA3AF",
                  marginRight: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Icon name="filter" size={12} color="#9CA3AF" />
                Scope:
              </span>
              {[
                { id: "all", label: "All", count: baseEntries.length },
                {
                  id: "global",
                  label: "Global",
                  count: baseEntries.filter((e) => e.is_global).length,
                  icon: "globe",
                },
                {
                  id: "org",
                  label: "Organization",
                  count: baseEntries.filter((e) => !e.is_global).length,
                  icon: "building",
                },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setActiveScope(s.id);
                    setPage(1);
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 10px",
                    border: `1px solid ${activeScope === s.id ? "#1D4ED8" : "#E5E7EB"}`,
                    borderRadius: 20,
                    background: activeScope === s.id ? "#EFF6FF" : "#fff",
                    color: activeScope === s.id ? "#1D4ED8" : "#6B7280",
                    fontSize: 11,
                    fontWeight: activeScope === s.id ? 700 : 400,
                    cursor: "pointer",
                  }}
                >
                  {s.icon && (
                    <Icon
                      name={s.icon}
                      size={11}
                      color={activeScope === s.id ? "#1D4ED8" : "#9CA3AF"}
                    />
                  )}
                  {s.label}
                  <span
                    style={{
                      background: activeScope === s.id ? "#BFDBFE" : "#F3F4F6",
                      color: activeScope === s.id ? "#1D4ED8" : "#9CA3AF",
                      padding: "0 5px",
                      borderRadius: 8,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {s.count}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Category sidebar */}
        {entries.length > 0 && (
          <div
            style={{
              width: 180,
              borderRight: "1px solid #E5E7EB",
              background: "#fff",
              padding: "14px 10px",
              flexShrink: 0,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#9CA3AF",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                padding: "2px 8px 8px",
              }}
            >
              Categories
            </div>
            <button
              onClick={() => {
                setActiveCat("all");
                setPage(1);
              }}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                padding: "7px 10px",
                borderRadius: 7,
                border: "none",
                cursor: "pointer",
                background: activeCat === "all" ? "#EFF6FF" : "transparent",
                color: activeCat === "all" ? "#1D4ED8" : "#374151",
                fontSize: 12,
                fontWeight: activeCat === "all" ? 700 : 400,
                marginBottom: 2,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <Icon
                  name="layers"
                  size={13}
                  color={activeCat === "all" ? "#1D4ED8" : "#9CA3AF"}
                />
                All
              </span>
              <span
                style={{
                  fontSize: 10,
                  background: activeCat === "all" ? "#BFDBFE" : "#F3F4F6",
                  color: activeCat === "all" ? "#1D4ED8" : "#9CA3AF",
                  padding: "1px 6px",
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                {scopeFiltered.length}
              </span>
            </button>
            {Object.entries(KB_CATEGORIES).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => {
                  setActiveCat(key);
                  setPage(1);
                }}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  padding: "7px 10px",
                  borderRadius: 7,
                  border: "none",
                  cursor: "pointer",
                  background: activeCat === key ? cat.bg : "transparent",
                  color: activeCat === key ? cat.color : "#374151",
                  fontSize: 12,
                  fontWeight: activeCat === key ? 700 : 400,
                  marginBottom: 2,
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <Icon
                    name={cat.icon}
                    size={13}
                    color={activeCat === key ? cat.color : "#9CA3AF"}
                  />
                  {cat.label}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    background: activeCat === key ? "#fff" : "#F3F4F6",
                    color: activeCat === key ? cat.color : "#9CA3AF",
                    padding: "1px 6px",
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  {catCounts[key] || 0}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Card grid */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {error ? (
            <ErrorState message={error} />
          ) : loading ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))",
                gap: 12,
              }}
            >
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} height={180} style={{ borderRadius: 10 }} />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                padding: "40px 20px",
              }}
            >
              <EmptyState
                icon="book"
                title="No knowledge base entries"
                subtitle="Create runbooks, root causes, and resolutions to help resolve operational issues."
              />
              <div style={{ textAlign: "center", marginTop: -20, marginBottom: 12 }}>
                <button
                  onClick={() => {
                    setEditEntry(null);
                    setShowModal(true);
                  }}
                  style={{
                    padding: "8px 18px",
                    background: "#1D4ED8",
                    color: "#fff",
                    border: "none",
                    borderRadius: 7,
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Add Entry
                </button>
              </div>
            </div>
          ) : catFiltered.length === 0 ? (
            searchResults ? (
              <EmptyState
                icon="search"
                title="No results found"
                subtitle="No matching items were found for your search query. Try clearing the search or adjusting filters."
              />
            ) : (
              <EmptyState
                icon="book"
                title="No entries found"
                subtitle={
                  activeCat !== "all"
                    ? "No entries in this category match the selected scope."
                    : "No entries match the selected filters."
                }
              />
            )
          ) : (
            <>
              {searchResults && groupedSearch ? (
                // Grouped search results
                Object.entries(groupedSearch).map(([catKey, catEntries]) => {
                  const cat = KB_CATEGORIES[catKey];
                  return (
                    <div key={catKey} style={{ marginBottom: 20 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          marginBottom: 10,
                          paddingBottom: 6,
                          borderBottom: `2px solid ${cat.border}`,
                        }}
                      >
                        <Icon name={cat.icon} size={14} color={cat.color} />
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: cat.color,
                          }}
                        >
                          {cat.label}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            background: cat.bg,
                            color: cat.color,
                            padding: "1px 6px",
                            borderRadius: 8,
                            fontWeight: 600,
                            border: `1px solid ${cat.border}`,
                          }}
                        >
                          {catEntries.length}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(280px,1fr))",
                          gap: 12,
                        }}
                      >
                        {catEntries.map((entry) => (
                          <KBCard
                            key={entry.id}
                            entry={entry}
                            onEdit={(e) => {
                              setEditEntry(e);
                              setShowModal(true);
                            }}
                            onDelete={handleDelete}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(280px,1fr))",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    {paged.map((entry) => (
                      <KBCard
                        key={entry.id}
                        entry={entry}
                        onEdit={(e) => {
                          setEditEntry(e);
                          setShowModal(true);
                        }}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                  <Pagination
                    page={page}
                    total={catFiltered.length}
                    pageSize={PAGE_SIZE}
                    onChange={setPage}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && (
        <KBModal
          entry={editEntry}
          onClose={() => {
            setShowModal(false);
            setEditEntry(null);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
